import type { MetricSummary, SliceMetrics } from '@/analysis/searchContract'
import { getSevLabel } from '@/constants'

const COMPOSITE_KEYS = [
  'demuxInRate',
  'demuxToDecodeLag',
  'renderRatio',
  'downloadKbps',
] as const

const METRIC_LABEL: Record<(typeof COMPOSITE_KEYS)[number], string> = {
  demuxInRate: 'demux in rate',
  demuxToDecodeLag: 'demux→decode lag',
  renderRatio: 'render ratio',
  downloadKbps: 'download kbps',
}

function reasonLabel(reason: string | null | undefined): string {
  if (reason === 'hard_floor') return 'hard floor'
  if (reason === 'primary') return 'threshold'
  return reason ? String(reason) : 'scored'
}

/**
 * Operator-facing explanation of why a slice is critical/warning/healthy.
 * Composite severity = worst (min) of the four scored pipeline metrics.
 */
export function explainSeverity(opts: {
  avgSeverity?: 0 | 1 | 2 | null
  worstMetric?: string | null
  worstMetricSeverity?: 0 | 1 | 2 | null
  metrics?: SliceMetrics | null
}): string {
  const lines: string[] = [
    `Average severity: ${getSevLabel(opts.avgSeverity ?? null)}`,
    `Worst metric: ${opts.worstMetric ?? '—'} (${getSevLabel(opts.worstMetricSeverity ?? null)})`,
    'Composite = worst of demux rate / demux→decode lag / render ratio / download (not FPS or glass-to-glass).',
  ]

  const m = opts.metrics
  if (m) {
    for (const key of COMPOSITE_KEYS) {
      const cell = m[key] as MetricSummary | undefined
      if (!cell) continue
      const tier = cell.tier
      const tierTxt = getSevLabel(tier ?? null)
      const val =
        typeof cell.displayValue === 'number' && Number.isFinite(cell.displayValue)
          ? String(cell.displayValue)
          : '—'
      const why = tier == null ? 'no data' : reasonLabel(cell.tierReason)
      lines.push(`${METRIC_LABEL[key]}: ${val} → ${tierTxt} (${why})`)
    }
  }

  return lines.join('\n')
}
